import { useElementSize } from '@/utils/use-element-size';
import { RefObject, useEffect, useRef, useState } from 'react';
import { CARD_ASPECT_RATIO } from './constants';
import { getGridSizeByType } from './helpers';

export type LayoutType = 'desktop' | 'mobile';

export type GridLayout = {
  cardWidth: number;
  cardHeight: number;
  gap: number;
  gridWidth: number;
  gridHeight: number;
  containerWidth: number;
  containerHeight: number;
  scaleFactor: number;
  radiusFactor: number;
  numItems: number;
  layout: LayoutType;
};

const calculateGridLayout = (
  containerWidth: number,
  containerHeight: number,
  grid: HTMLElement | null,
  numItems: number
): GridLayout => {
  const style = grid ? getComputedStyle(grid) : null;

  let gap = 0;
  let cardWidth = 0;
  let layout: LayoutType = 'desktop';

  if (style) {
    // taking some values from the DOM, preventing duplicate sources of truth
    cardWidth = parseFloat(style.getPropertyValue('width'));
    gap = parseFloat(style.getPropertyValue('height'));
    const l = style.getPropertyValue('--layout');
    if (l !== 'desktop' && l !== 'mobile') {
      throw new Error(`Invalid layout ${l}`);
    }
    layout = l;
  }

  const [columns, rows] = getGridSizeByType(layout);
  const cardHeight = cardWidth * CARD_ASPECT_RATIO;
  const scaleFactor = 3.5;
  const radiusFactor = 2.5;
  const gridWidth = (cardWidth + gap) * columns - gap;
  const gridHeight = (cardHeight + gap) * rows - gap;

  const gridLayout = {
    cardWidth,
    cardHeight,
    gap,
    gridWidth,
    gridHeight,
    containerWidth,
    containerHeight,
    scaleFactor,
    radiusFactor,
    numItems,
    layout,
  };

  return gridLayout;
};

export function useGridLayout<TRootEl extends HTMLElement, TGridEl extends HTMLElement>(
  numItems: number
): [root: RefObject<TRootEl>, grid: RefObject<TGridEl>, layout: GridLayout] {
  const grid = useRef<TGridEl>(null);
  const [root, { width, height }] = useElementSize<TRootEl>();

  const [gridLayout, setGridLayout] = useState<GridLayout>(() => {
    return calculateGridLayout(width, height, grid.current, numItems);
  });

  useEffect(() => {
    if (width && height) {
      const layout = calculateGridLayout(width, height, grid.current, numItems);
      setGridLayout(layout);
    }
  }, [width, height, grid, numItems]);

  return [root, grid, gridLayout];
}
